import { isEmpty, values } from 'ramda';
import React, { FormEvent, ReactElement, useContext, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { createStaffUser } from '../../graphql/staffUser';
import { getCurrentUserId, StaffUserErrors, StaffUserInitialErrors, validateStaffUserInput } from '../../utils/users';
import Button from '../bits/Buttons/Button';
import Form from '../bits/Form/Form';
import TextInput from '../bits/FormFields/TextInput';
import { AdminStateContext } from '../WithAdminState/adminState';
import AuthContext from '../WithAuth/AuthContext';
import { getRouteOrganisationDetail, ROUTE_STAFF } from '../../utils/routes';
import { OrgDetailTabs } from '../templates/OrganisationDetails';

const CreateStaffUser = (): ReactElement => {
    const history = useHistory();
    const { orgUuid }: { orgUuid: string } = useParams();
    const { currentAuthenticatedUser: user, isAdmin } = useContext(AuthContext);
    const { organisations, setCurrentOrganisation } = useContext(AdminStateContext);
    const [errors, setErrors] = useState<StaffUserErrors>(StaffUserInitialErrors);
    const [createStaffUserMutation, { loading, error: CreateStaffUserError }] = createStaffUser.hook();
    const backLink = isAdmin ? `${getRouteOrganisationDetail(orgUuid)}?tab=${OrgDetailTabs.staff}` : ROUTE_STAFF;
    const orgName = organisations?.[orgUuid]?.name;
    const orgLabel = orgName ? ` to ${orgName}` : '';

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');

    const formBody = (
        <>
            <TextInput
                label="Name"
                id="name"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                error={errors?.nameError}
            />
            <TextInput
                label="Email address"
                id="staffEmail"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value.trim())}
            />
            <Button buttonStyle="primary" text="Save" type="submit" />
        </>
    );

    const handleComplete = () => {
        if (isAdmin) {
            setCurrentOrganisation(organisations[orgUuid]);
        }

        history.push(backLink);
    };

    const validateAndCreateStaffUser = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const variables = {
            staffUser: {
                name,
                createdBy: getCurrentUserId(user),
                orgUuid,
                isAdmin: false,
                isActive: true,
                email,
            },
        };
        const validationResult = validateStaffUserInput(variables);

        if (values(validationResult).every(isEmpty)) {
            const createdStaff = await createStaffUserMutation({
                variables,
                refetchQueries: ['listAllStaffUsers', 'listStaffUsersByOrg'],
            });

            if (createdStaff.data) {
                handleComplete();
            }
        } else {
            setErrors(validationResult);
        }
    };

    return (
        <Form
            title="New Staff User"
            description={`Use this form to add a new staff member${orgLabel}.`}
            body={formBody}
            onCancel={() => history.push(backLink)}
            onSubmit={validateAndCreateStaffUser}
            cancelTitle="Cancel and discard changes"
            loading={loading}
            error={!!CreateStaffUserError}
            errorMessage={CreateStaffUserError?.message}
        />
    );
};

export default CreateStaffUser;
